import { useContext, useEffect, useMemo, useState } from 'react';

import { Alert } from '@mui/lab'

import { gql } from '@apollo/client';

import { MusicNote } from '@mui/icons-material';

import { useQuery } from '@apollo/client';

import { Box, Button, Link as MaterialLink, Table, Typography, Container, Paper, Breadcrumbs } from '@mui/material';

import { NodeTable } from '@/components/NodeTable';

import i18n from '@/i18n';

import { Link } from '@/components';
import { useParams } from 'react-router';

import { Statistics } from '@/components/Statistics';
import { useSearchParams } from 'react-router-dom';
import { StatisticsContainer } from '@/pages/dashboard/statistics';
import moment from 'moment';
import { removeNull } from '@/utils';
import { ErrorView } from '@/components/ErrorView';
import { DiscoveryModeContext } from '@/contexts/DiscoveryModeContext';

const QUERY = gql`
  query getDiscoveryModeCampaign(
    $campaignId: ID!,
    $organizationIds: [ID],
    $breakdown: String,
    $labelIds: [ID],
    $stage: String!,
    $entity: String,
    $artistIds: [ID],
    $parentNodeType: String,
    $parentNodeId: ID,
    $sortBy: String
  ) {
    spotify {
      discoveryModeCampaign(campaignId: $campaignId) {
        id
        start
        end
        midDataAvailable
        endDataAvailable
        rows(
          entity: $entity,
          organizationIds: $organizationIds,
          labelIds: $labelIds,
          parentNodeType: $parentNodeType,
          breakdown: $breakdown,
          parentNodeId: $parentNodeId,
          artistIds: $artistIds,
          sortBy: $sortBy
          stage: $stage
        ) {
          id
          name
          type
          artistName
          labelName
          promotedStreams
          promotedStreamsUplift
          platformStreams
          platformStreamsUplift
          campaignStreamUplift
          spotifyId
          intentRate
          label {
            id
            slug
            name
            fugaId
          }
          organization {
            id
            slug
            name
          }
          artists {
            slug
            name
          }
          royalty
          amount
          price
          discount
          fee
          net
          organizationId
          labelId
          releaseId
          labelFugaId
        }
      }
    }
  }
`

export function DiscoveryModeCampaignPage() {
  const { campaignId, entity, parentNodeType, parentNodeId } = useParams()
  const [searchParams] = useSearchParams()

  const {
    start,
    end,
    selectedOrganizations,
    selectedLabels,
    selectedArtists,
    breakdown,
    stage,
    midDataAvailable,
    endDataAvailable
  } = useContext(DiscoveryModeContext)

  const sortBy = removeNull(searchParams.get('sortBy')) ?? null

  return ( 
    <DiscoveryModeCampaignContainer
      campaignId={campaignId}
      sortBy={sortBy}
      start={start}
      end={end}
      stage={stage ?? 'MID'}
      selectedOrganizations={selectedOrganizations}
      selectedLabels={selectedLabels}
      selectedArtists={selectedArtists}
      breakdown={breakdown}
      entity={entity}
      parentNodeType={parentNodeType}
      parentNodeId={parentNodeId}
      endDataAvailable={endDataAvailable}
    /> 
  )
}

export function DiscoveryModeCampaignContainer({
  campaignId,
  sortBy,
  entity = "label_name",
  breakdown,
  parentNodeId,
  stage = 'MID',
  selectedArtists,
  selectedOrganizations,
  selectedLabels,
  parentNodeType
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    setSelectedOrganizations,
    setSelectedLabels,
    setSelectedArtists,
    setEndDataAvailable,
    setMidDataAvailable,
    setStage
  } = useContext(DiscoveryModeContext)

  const { data, error, previousData } = useQuery(QUERY, {
    variables: {
      entity,
      campaignId,
      artistIds: selectedArtists?.map(a => a.slug),
      organizationIds: selectedOrganizations?.map(o => o.slug),
      stage,
      organizationId: selectedOrganizations?.[0]?.slug,
      labelIds: selectedLabels?.map(l => l.slug),
      sortBy,
      breakdown,
      parentNodeId,
      parentNodeType
    }
  })

  const campaign = (data ?? previousData)?.spotify?.discoveryModeCampaign ?? {
    rows: []
  }

  useEffect(() => {
    setMidDataAvailable(campaign.midDataAvailable)
    setEndDataAvailable(campaign.endDataAvailable)
    if (!campaign.endDataAvailable && stage != 'MID') {
      setStage('MID', { replace: true })
    } else if (!campaign.midDataAvailable && stage != 'END') {
      setStage('END', { replace: true })
    }
  }, [campaign])

  const handleSortByChanged = ({ sortByFieldId, sortByDirection }) => {
    let sortBy = ''
    if (sortByDirection == 'desc') {
      sortBy += '-'
    }
    if (sortByFieldId?.length > 0) {
      sortBy += sortByFieldId
    } else {
      sortBy = null
    }
    if (sortBy !== searchParams.get('sortBy')) {
      setSearchParams(params => {
        if (sortBy?.length > 0) {
          params.set('sortBy', sortBy)
        } else {
          params.delete('sortBy')
        }
        return params
      })
    }
  }

  const onNodesChecked = () => {}
  const onPerformAction = () => {}

  return (
    <Container maxWidth="xl">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography>{i18n.t('discovery-mode')}</Typography>
        <MaterialLink component={Link} to={{ pathname: '/dashboard/discovery-mode/campaigns', search: searchParams.toString()}}>{i18n.t('campaigns')}</MaterialLink>
        <Typography>{campaign.start} - {campaign.end}</Typography>
      </Breadcrumbs>
      {(() => {
        if (campaign) {
          const rows = campaign.rows

          const tops = [...rows].filter(a => a.promotedStreamsUplift > 0)

          tops.sort((a, b) => -(a.promotedStreamsUplift - b.promotedStreamsUplift))

          const streams = rows.reduce((sum, row) => (row.promotedStreams ?? 0) + sum, 0)
          const gross = rows.reduce((sum, row) => (row.amount ?? 0) + sum, 0)
          const discount = rows.reduce((sum, row) => (row.discount ?? 0) + sum, 0)
          const net = rows.reduce((sum, row) => ((row.amount ?? 0) - (row.discount)) + sum, 0)
          const currency = 'EUR'

          if (error) {
            return (
              <ErrorView error={i18n.t('there-was-an-error-loading-the-campaign-data')} />
            )
          }

          return (
            <>
              <Typography level="h1">Top {breakdown ?? 'track'}s in discovery mode report in {parentNodeId} {parentNodeType}</Typography>
              <Paper>
                <NodeTable
                  sortLocal={true}
                  defaultSortByFieldId={searchParams.get('sortBy')?.replace(/-/, '')}
                  defaultSortByDirection={searchParams.get('sortBy')?.startsWith('-') ? 'desc' : 'asc'}
                  onSortByChanged={handleSortByChanged}
                  onNodesChecked={onNodesChecked}
                  onPerformAction={onPerformAction}
                  canCheck={true}  
                  renderRowClassNames={({ node }) => {    
                    return node.promotedStreamsUplift > 0 ? 'row-info' : 'row-warning'
                  }}
                  emptyHeading={i18n.t('no-data-found')}
                  emptyText={i18n.t('no-data-was-found-for-this-discovery-mode-campaign-with-the-selected-filters')}
                  emptyIcon={MusicNote}
                  isRowUnavailable={(row) => {
                    return false        
                  }}
                  toolbarButtons={() => <></>}
                  sx={{
                    '& .row-success': {
                      bgcolor: 'rgba(0, 255, 0, .1)'
                    },
                    '& .row-error': {
                      bgcolor: 'rgba(255, 0, 0, .1)'
                    },
                    '& .row-warning': {
                      bgcolor: 'rgba(255, 255, 0, .1)'
                    }
                  }}
                  actions={[ 
                  ]}
                  columns={[
                    {
                      id: 'name',
                      name: i18n.t('name'),
                      type: 'name',
                      sortable: true,
                      render: ({ node, column }) => {
                        return (
                          <Typography>{node[column.id]}</Typography>
                        )
                      }
                    },
                    {
                      id: 'artists',
                      name: i18n.t('artists'),
                      sortable: true,
                      type: 'belongsToMany',
                      render: ({ node, column }) => {
                        return (
                          <Typography>
                            {node.artistName}
                          </Typography>
                        )
                      }
                    },
                    {
                      id: 'organization',
                      sortable: true,
                      name: i18n.t('organization'),
                      type: 'belongsTo',
                      render: ({ node, column }) => {
                        return (
                          <Typography>
                            {node[column.id]?.name}
                          </Typography>
                        )
                      }
                    },
                    {
                      id: 'label',
                      sortable: true,
                      name: i18n.t('label'),
                      type: 'belongsTo',
                      render: ({ node, column }) => {
                        return (
                          <Typography>
                            {node[column.id]?.name}
                          </Typography>
                        )
                      }
                    },
                    {
                      id: 'promotedStreams',
                      sortable: true,
                      name: i18n.t('promoted-streams'),
                      type: 'number',
                      render: ({ node, column }) => {
                        return (
                          <span style={{ textAlign: 'right' }}>
                            <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                          </span>
                        )
                      } 
                    },
                    {
                      id: 'promotedStreamsUplift',
                      sortable: true,
                      name: i18n.t('promoted-streams-uplift'),
                      type: 'number',
                      render: ({ node, column }) => {
                        return (
                          <span style={{ textAlign: 'right' }}>
                            <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</Typography>
                          </span>
                        )
                      }
                    },
                    {
                      id: 'campaignStreamUplift',
                      sortable: true,
                      name: i18n.t('campaign-stream-uplift'),
                      type: 'number',
                      render: ({ node, column }) => {
                        return (
                          <span style={{ textAlign: 'right' }}>
                            <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</Typography>
                          </span>
                        )
                      }
                    }/*,
                    {
                      id: 'platformStreams',
                      sortable: true,
                      name: i18n.t('platform-streams'),
                      type: 'text',
                      render: ({ node, column }) => { 
                        return (
                          <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                        )
                      }
                    }, 
                    {
                      id: 'amount',
                      sortable: true,
                      name: i18n.t('amount'),
                      type: 'text',
                      render: ({ node, column }) => { 
                        return (
                          <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                        )
                      }
                    }, 
                    {
                      id: 'discount',
                      sortable: true,
                      name: i18n.t('discount'),
                      type: 'text',
                      render: ({ node, column }) => { 
                        return (
                          <Typography textAlign="right">{(node[column.id] ?? 0)?.toLocaleString(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                        )
                      }
                    }, 
                    {
                      id: 'net',
                      sortable: true,
                      name: i18n.t('net'),
                      type: 'text',
                      render: ({ node, column }) => { 
                        return (
                          <Typography textAlign="right">{((node.amount ?? 0) - (node.discount ?? 0))?.toLocaleString(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                        )
                      }
                    }*/, 
                    {
                      id: 'intentRate',
                      sortable: true,
                      name: i18n.t('intent-rate'),
                      type: 'number',
                      render: ({ node, column }) => { 
                        return (
                          <Typography textAlign="right">{(node[column.id])?.toLocaleString(undefined, {  minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                        )
                      }
                    }
                  ]}
                  nodes={rows}
                />
              </Paper>
            </>
          )
        }
      })()}
    </Container>
  )
}

export default DiscoveryModeCampaignPage;
